/* UnderlaySync だけを namespace に公開し、色抽出などの内部実装は IIFE に閉じる */
(() => {
  if (window.AuremeMainVisualUnderlay) return;

  /* contract.js の DEFAULT_CONSTANTS と同じ値にし、先後どちらの評価順でも同じ ready 通知にする */
  const {
    UNDERLAY_READY_EVENT = 'aureme:main-visual-underlay:ready',
    UNDERLAY_READY_CLASS = 'aureme-main-visual-underlay-ready',
  } = window.AuremeMainVisual?.Sync?.constants || {};

  const UNDERLAY_SAMPLE_CANVAS_SIZE = 16;
  const UNDERLAY_SAMPLE_EDGE_POINTS = [
    [1, 1],
    [8, 1],
    [14, 1],
    [1, 8],
    [14, 8],
    [1, 14],
    [8, 14],
    [14, 14],
  ];

  class UnderlayColorSampler {
    async sample(src) {
      const image = await this.loadImage(src);
      return this.sampleImageEdgeColor(image);
    }

    loadImage(src) {
      return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.decoding = 'async';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = src;
      });
    }

    sampleImageEdgeColor(image) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;

      canvas.width = UNDERLAY_SAMPLE_CANVAS_SIZE;
      canvas.height = UNDERLAY_SAMPLE_CANVAS_SIZE;
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      /* AUREME のメインビジュアル下地は画像外周の平均色を使い、読み込み中の白チラつきを目立たせない */
      const total = UNDERLAY_SAMPLE_EDGE_POINTS.reduce(
        (accumulator, [x, y]) => {
          const [red, green, blue, alpha] = context.getImageData(x, y, 1, 1).data;
          if (alpha === 0) return accumulator;

          accumulator.red += red;
          accumulator.green += green;
          accumulator.blue += blue;
          accumulator.count += 1;
          return accumulator;
        },
        { red: 0, green: 0, blue: 0, count: 0 },
      );

      if (total.count === 0) return null;

      return {
        red: Math.round(total.red / total.count),
        green: Math.round(total.green / total.count),
        blue: Math.round(total.blue / total.count),
      };
    }
  }

  class UnderlaySync {
    constructor(sectionElement, headerSection) {
      this.sectionElement = sectionElement;
      this.headerSection = headerSection;
      this.colorSampler = new UnderlayColorSampler();
      this.pendingMedia = null;
      this.shouldSyncReadyState = true;
      this.handleActiveMediaReady = this.handleActiveMediaReady.bind(this);
    }

    reset() {
      this.shouldSyncReadyState = true;
      this.clear();
    }

    clear() {
      this.removePendingMediaListeners();
      this.updateUnderlayReadyState(false);
    }

    sync(activeMedia, hasMediaForActiveBreakpoint) {
      if (!this.shouldSyncReadyState) return;

      if (!hasMediaForActiveBreakpoint || !activeMedia || this.isMediaReady(activeMedia)) {
        this.markReady();
        return;
      }

      this.watchActiveMedia(activeMedia);
    }

    syncUnderlayColors(sources) {
      for (const [viewport, source] of Object.entries(sources)) {
        this.syncUnderlayColor(viewport, source);
      }
    }

    handleActiveMediaReady() {
      this.markReady();
    }

    markReady() {
      this.removePendingMediaListeners();
      this.updateUnderlayReadyState(true);

      this.shouldSyncReadyState = false;
    }

    updateUnderlayReadyState(isReady) {
      this.toggleClass(this.headerSection, UNDERLAY_READY_CLASS, isReady);
      this.toggleClass(this.sectionElement, UNDERLAY_READY_CLASS, isReady);
    }

    toggleClass(element, className, force) {
      if (!element) return;
      element.classList.toggle(className, force);
    }

    watchActiveMedia(media) {
      if (this.pendingMedia === media) return;

      this.removePendingMediaListeners();
      this.pendingMedia = media;

      /* 動画はフレーム取得後にだけ下地を外し、黒い空フレームの露出を防ぐ */
      const readyEvents = { IMG: ['load'], VIDEO: ['loadeddata'] }[media.tagName];
      if (!readyEvents) {
        this.markReady();
        return;
      }

      for (const eventName of readyEvents) {
        media.addEventListener(eventName, this.handleActiveMediaReady, { once: true });
      }
      media.addEventListener('error', this.handleActiveMediaReady, { once: true });
    }

    removePendingMediaListeners() {
      if (!this.pendingMedia) return;

      /* 直前 viewport の待機イベントを残さず、切替後に古い ready 判定が混線するのを防ぐ */
      this.pendingMedia.removeEventListener('load', this.handleActiveMediaReady);
      this.pendingMedia.removeEventListener('loadeddata', this.handleActiveMediaReady);
      this.pendingMedia.removeEventListener('error', this.handleActiveMediaReady);
      this.pendingMedia = null;
    }

    isMediaReady(media) {
      switch (media.tagName) {
        case 'IMG':
          return media.complete;
        case 'VIDEO':
          return media.readyState >= 2 || media.error != null;
        default:
          return true;
      }
    }

    async syncUnderlayColor(viewport, src) {
      if (!src) return;

      try {
        const sampledColor = await this.colorSampler.sample(src);
        if (!sampledColor) return;

        const underlayColorValue = `rgb(${sampledColor.red} ${sampledColor.green} ${sampledColor.blue})`;
        const underlayVariableName = `--aureme-main-visual-underlay-background-${viewport}`;

        this.sectionElement?.style.setProperty(underlayVariableName, underlayColorValue);
      } catch (error) {
        console.debug('Aureme main visual underlay color fallback remained default.', error);
      }
    }
  }

  window.AuremeMainVisualUnderlay = {
    UnderlaySync,
  };
  window.dispatchEvent(new Event(UNDERLAY_READY_EVENT));
})();
